import { CustomerSettingsData } from 'src/app/services/customer-settings.service';
import { ConstantsService } from 'src/app/services/constants.service';
import { ResourceManagerService } from 'src/app/services/resource.manager.service';
import * as dayjs from 'dayjs';
import { Session } from './resource.model';

export class ResourceUnavailableApt {
  Id: number;
  BranchId: number;
  ResourceId: number;
  Date: Date;
  StartTime: Date;
  EndTime: Date;
  Reason: string;
  dtoStart: dtoDate;
  dtoEnd: dtoDate;

  static CreateFromAppointment(apt: Appointment): ResourceUnavailableApt {
    const r: ResourceUnavailableApt = new ResourceUnavailableApt();
    r.BranchId = apt.Branch;
    r.Date = apt.StartDateTime;
    r.StartTime = new Date(new Date(apt.StartDateTime).setFullYear(1970, 0, 1));
    r.EndTime = new Date(new Date(apt.EndDateTime).setFullYear(1970, 0, 1));
    r.ResourceId = apt.ResourceId;
    r.Reason = apt.SessionName;
    r.Id = apt.AppointmentId * -1;
    r.dtoStart = dtoDate.CreateFrom(apt.StartDateTime);
    r.dtoEnd = dtoDate.CreateFrom(apt.EndDateTime);
    return r;
  }
}

export class ResourceWorkTime {
  StartTime: Date;  // Seconds from midnight
  EndTime: Date;    // Seconds from midnight
}

export class ResourceUnavailability {
  public StartDateTime: Date;
  public Duration: number;
  public Branch: number;
  public Resource: number;
}
export class ResourceInfo {
  ResourceId: number = -1;
  ResourceName: string = "Unknown";
  BranchId: number = -1;
  NetworkId: number = -1;
  ResourceTypeId: number = -1;
  ColourHex: string = "";
  WorkTime: ResourceWorkTime[] = [];
  WorkTimeOnline: ResourceWorkTime[] = [];
  TMaxStatus = 'BedDirty';
  TMaxTimeLeft: number = 0;
  TMaxTimeLeftString = '';
  UseQueueManager: boolean = false;
}

export class AptView {
  Value: number;
  Name: string;
  BranchId: number;

  public static Create(apt: AptView): AptView {
    const a: AptView = new AptView();
    a.Value = apt.Value;
    a.BranchId = apt.BranchId;
    a.Name = apt.Name;
    return a;
  }
}

export class dtoDate {
  dtYear: number;
  dtMonth: number;
  dtDay: number;
  dtHour: number;
  dtMinute: number;
  dtSecond: number;

  static CreateFrom(d: Date): dtoDate {
    var dto = new dtoDate();
    if (d) {
      var dd = new Date(d);
      dto.dtYear = dd.getFullYear();
      dto.dtMonth = dd.getMonth() + 1; // month is zero based (Jan =0, 1=Feb), but WebApi uses 1 based index (Jan=1, Feb=2)
      dto.dtDay = dd.getDate();
      dto.dtHour = dd.getHours();
      dto.dtMinute = dd.getMinutes();
      dto.dtSecond = dd.getSeconds();
    }

    return dto;
  }
}

export enum AppointmentState { New = 0, Booked = 10, Attended = 20, Paid = 30, InProgress = 40, Completed = 50, Cancelled = 999 }
export class Appointment {
  StartDateTime: Date;
  AttendedDateTime: Date;
  EndDateTime: Date;
  Duration: number;
  ClientName: string;
  ClientId: number;
  ResourceId: number;
  ResourceName: string;
  SessionId: number;
  SessionName: string;
  AppointmentId: number;
  ResourceUnavailable: boolean;
  PreSessionDelay: number;
  PostSessionDelay: number;
  NetworkId: number;
  Terminal: number;
  BookingMethod: number;
  Branch: number;
  CurrentApt: boolean;
  UseQMgr: boolean;
  CreditInfo: string;
  Deposit: number;
  State: AppointmentState;
  StateDesc: string;
  color: string;
  Locked: boolean;
  IsClientBirthday: boolean;

  public static GetStateDesc(a: AppointmentState): string {
    switch (a) {
      case AppointmentState.Booked:
        return 'Booked';
      case AppointmentState.Attended:
        return 'Attended';
      case AppointmentState.Paid:
        return 'Paid';
      case AppointmentState.InProgress:
        return 'In Progress';
      case AppointmentState.Completed:
        return 'Completed';
      default:
        return 'Unknown';
    }
  }

  public static IsAptBeingProcessedOrCompleted(apt: Appointment): boolean {
    return (apt.State >= AppointmentState.InProgress)
  }

  public static CanMakeSale(apt: Appointment, customerSettings: CustomerSettingsData, session: Session, resourceMgr: ResourceManagerService): boolean {
    if (apt.ResourceUnavailable) {
      return false;
    }
    return this.CanMakeSaleEx(apt.State, apt.StartDateTime, customerSettings, session, resourceMgr);
  }

  public static CanMakeSaleEx(aptState: AppointmentState, StartDateTime: Date, customerSettings: CustomerSettingsData, session: Session, resourceMgr: ResourceManagerService): boolean {
    if (
      aptState === AppointmentState.Attended ||
      aptState === AppointmentState.Completed ||
      aptState === AppointmentState.Booked ||
      aptState === AppointmentState.InProgress ||
      aptState === AppointmentState.New ||
      aptState === AppointmentState.Paid
    ) {
      if (!ConstantsService.isToday(StartDateTime)) {
        return false;
      }

      // Do not allow MakeSale when apt session is made inactive after booking
      if (aptState === AppointmentState.Booked && (session && !session.Active)) {
        return false
      }

      if (customerSettings.LimitPOSAptSale === true 
        && resourceMgr.IsResourceTypePaymentAfterSession(session.ResourceTypeId) === false) {
        let m = Appointment.GetMinutesBetweenAptAndTime(StartDateTime);
        if (m < 0) {
          m = m * -1;
        }
        return (m < customerSettings.LimitPOSAptSaleTime);
      }
      else
        return true;
    }
    return false;
  }

  public static CanAttend(apt: Appointment): boolean {
    if (apt.ResourceUnavailable) {
      return false;
    }
    return Appointment.CanAttendEx(apt.State, apt.StartDateTime);
  }

  public static CanAttendEx(State: AppointmentState, StartDateTime: Date): boolean {
    // Can only attend appointment if Booked and with +/- 30mins of now.
    if (State === AppointmentState.Booked) {
      let m = Appointment.GetMinutesBetweenAptAndTime(StartDateTime);
      if (m < 0) {
        m = m * -1;
      }
      return (m < 30);
    }
    return false;
  }

  // Determine if we can start this appointment - Resource needs to be in 'Ready' state to start, and appointment needs to be paid
  public static CanStart(apt: Appointment, resourceMgr: ResourceManagerService, bStartWhenReadyOrDirty: boolean = false): boolean {
    if (apt.ResourceUnavailable) { return false; }
    if (
      (apt.State !== AppointmentState.Paid && !resourceMgr.IsResourcePaymentAfterSession(apt.ResourceId)) ||
      apt.State === AppointmentState.Completed
    ) {
      return false;
    }
    if (bStartWhenReadyOrDirty == false)
      return resourceMgr.IsResourceReady(apt.ResourceId)
    else
      return resourceMgr.IsResourceReady(apt.ResourceId) || resourceMgr.IsResourceDirty(apt.ResourceId);
  }

  public static CanStop(apt: Appointment, resourceMgr: ResourceManagerService): boolean {
    if (apt.ResourceUnavailable) { return false; }
    if (apt.State !== AppointmentState.InProgress) { return false; }
    if (apt.ResourceId >= 0) { return resourceMgr.CanStopResource(apt.ResourceId); }
    return true;
  }

  public static CanCancel(apt: Appointment, customerSettings: CustomerSettingsData): boolean {
    // We can only cancel appointments that are in the future and have not made sale
    if (apt.ResourceUnavailable) {
      return false;
    }

    return this.CanCancelEx(apt.State, apt.StartDateTime, customerSettings);
  }

  public static CanCancelEx(State: AppointmentState, StartDateTime: Date, customerSettings: CustomerSettingsData): boolean {
    // We can only cancel appointments that are in the next 30 min in the future and have not made sale, but but
    if (State >= AppointmentState.Paid) {
      return false;
    }

    if (State === AppointmentState.New) {
      return false;
    }

    if (customerSettings.LimitAppointmentCancellation === true) {
      const diff = Appointment.GetMinutesBetweenAptAndTime(StartDateTime);
      return (diff <= customerSettings.LimitAppointmentCancellationTime);
    }
    return true;
  }

  public static GetMinutesBetweenAptAndTime(aptTime: Date, dt: Date | null = null): number {
    const msdt = dayjs(aptTime);
    let dt2 = new Date();
    if (dt != null) { dt2 = dt; }
    const mdt = dayjs(dt2);
    const m = mdt.diff(msdt, 'minutes', false);
    return m;
  }

  static CreateAppointment(apt: any): Appointment {
    const a: Appointment = new Appointment();

    a.ClientName = apt.ClientName;

    a.StartDateTime = apt.StartDateTime;
    a.AttendedDateTime = apt.AttendedDateTime;
    a.EndDateTime = apt.EndDateTime;

    // a.StartDateTime = ConstantsService.MakeDateTimeGMT(apt.StartDateTime);
    // a.AttendedDateTime = ConstantsService.MakeDateTimeGMT(apt.AttendedDateTime);
    // a.EndDateTime = ConstantsService.MakeDateTimeGMT(apt.EndDateTime);

    a.Duration = apt.Duration;
    a.ResourceName = apt.ResourceName;
    a.SessionName = apt.SessionName;
    a.AppointmentId = apt.AppointmentId;
    a.Branch = apt.Branch;
    a.ResourceUnavailable = apt.ResourceUnavailable;
    a.ClientId = apt.ClientId;
    a.SessionId = apt.SessionId;
    a.ResourceId = apt.ResourceId;
    a.State = apt.State;
    a.color = apt.color;
    a.PreSessionDelay = apt.PreSessionDelay;
    a.PostSessionDelay = apt.PostSessionDelay;
    a.Terminal = apt.Terminal;
    a.BookingMethod = apt.BookingMethod;
    a.NetworkId = apt.NetworkId;
    a.CurrentApt = apt.CurrentApt;
    a.UseQMgr = apt.UseQMgr;
    a.CreditInfo = apt.CreditInfo;
    a.Locked = apt.Locked;
    a.IsClientBirthday = apt.IsClientBirthday;
    return a;
  }

  public updateColour(customerSettings: CustomerSettingsData): string {
    let clr = '#383c48';
    switch (this.State) {
      case AppointmentState.Booked:
        clr = customerSettings.aptStateBooked + '!important;';
        break;
      case AppointmentState.Attended:
        clr = customerSettings.aptStateAttended + '!important;';
        break;
      case AppointmentState.Paid:
        clr = customerSettings.aptStatePaid + '!important;';
        break;
      case AppointmentState.InProgress:
        clr = customerSettings.aptStateInProgress + '!important;';
        break;
      case AppointmentState.Completed:
        clr = customerSettings.aptStateCompleted + '!important;';
        break;
    }
    this.color = clr;
    return clr;
  }
}
